import React from 'react'
import styled from 'styled-components'

import { legalAgreementIds } from '../../../../../utils/legalAgreements'
import { ClickWrapContentComponent } from '../../../clickwrap/types'
import * as Styled from '../styled'

const RxDFHendUSMay2021Content: ClickWrapContentComponent<{ className?: string }> = ({
  className,
}) => {
  return (
    <Styled.ContentContainer className={className}>
      <h1>RxDFHend Agreement</h1>
      <p>
        <strong>
          Practitioner Wholesale Sales Agreement Between You and Designs for Health, Inc.
        </strong>
      </p>
      <p>
        THIS AGREEMENT TAKES EFFECT WHEN YOU CLICK THE “I AGREE” BUTTON BELOW (“EFFECTIVE DATE”). BY
        CLICKING ON THE “I AGREE” BUTTON BELOW YOU: (A) ACKNOWLEDGE THAT YOU HAVE READ AND
        UNDERSTAND THIS AGREEMENT; (B) REPRESENT AND WARRANT THAT YOU HAVE THE RIGHT, POWER, AND
        AUTHORITY TO ENTER INTO THIS AGREEMENT ON BEHALF OF THE ACCOUNT HOLDER AND, IF ENTERING INTO
        THIS AGREEMENT FOR AN ENTITY, THAT YOU HAVE THE LEGAL AUTHORITY TO BIND THAT ENTITY; AND (C)
        ACCEPT THIS AGREEMENT AND AGREE THAT YOU ARE LEGALLY BOUND BY ITS TERMS.
      </p>
      <p>
        IF YOU DO NOT ACCEPT THESE TERMS, YOU MAY NOT ESTABLISH A PRACTITIONER ACCOUNT WITH DESIGNS
        FOR HEALTH TO PURCHASE PRODUCTS.
      </p>
      <p>
        This RxDFHend Agreement is a Practitioner Wholesale Sales Agreement (“Agreement”) and is a
        binding contract between the individual or entity that is entering into this Agreement
        (“You” or “Customer”) and Designs for Health, Inc. (“DFH”) and seeks to establish a
        practitioner account (“Account”) to buy products made available for sale by DFH or its
        affiliates to Account holders (“Products”).
      </p>
      <p>
        In addition to this Agreement, all sales of Products are also subject to and governed by the{' '}
        <strong>Terms of Sale – Wholesale Accounts</strong> (“Terms of Sale”), available at{' '}
        <a
          href="https://www.designsforhealth.com/terms-of-sale-wholesale-accounts"
          target="_blank"
          rel="noreferrer"
        >
          https://www.designsforhealth.com/terms-of-sale-wholesale-accounts
        </a>{' '}
        or such other URL as DFH may designate from time to time, which are incorporated herein by
        reference and form part of this Agreement. To the extent the terms of this RxDFHend
        Agreement conflict with the Terms of Sale, the provisions of this RxDFHend Agreement shall
        prevail, in both cases where this RxDFHend Agreement is expressly identified as superseding
        the Terms of Sale and where not expressly identified but so conflicting.
      </p>
      <p>
        <strong>1. Customer’s Account</strong>
      </p>
      <p>
        (a) <span className="text-italic">Qualifications</span>. Customer or, if Customer is an
        entity, at least one employee of or contractor affiliated with Customer must have
        credentials that meet DFH’s requirements to qualify as a health care practitioner (“QHP”).
        Only QHPs are eligible to establish and maintain an Account. The sufficiency of QHP
        credentials shall be determined by DFH in its sole and absolute discretion. Customer shall
        immediately notify DFH of the loss of any credential of a QHP submitted to DFH when Customer
        applied to open the Account, or if the QHP whose credential was submitted is no longer
        employed by or affiliated with Customer. Customer shall, at its own expense, obtain and
        maintain required certifications, credentials, licenses, and permits necessary to conduct
        business in accordance with the Agreement.
      </p>
      <p>
        (b) <span className="text-italic">Authorized Users</span>. Only Customer and Customer’s
        authorized agents may access the Account or use the Account to purchase Products
        (“Authorized Users”). Customer shall not share Account access credentials or permit Account
        use by any person except for Authorized Users. Customer is responsible for all orders placed
        through the Account, whether by Authorized Users or others, and for compliance by all such
        persons with the Agreement. Customer shall prevent unauthorized use of the Account and shall
        promptly notify DFH in writing if Customer knows or has reason to believe that Customer’s
        Account has been used by anyone other than an Authorized User. Customer agrees to provide
        reasonable assistance to DFH upon request to identify unauthorized users of the Account and
        to take corrective actions that DFH deems appropriate.
      </p>
      <p>
        <strong>2. Customer’s Responsibilities</strong>
      </p>
      <p className="text-underline">(a) Resale Restrictions</p>
      <p className="text-indent">
        (i) <span className="text-italic">Resale Only in the United States</span>. Customer shall
        not list, offer for sale, sell, distribute, or otherwise make available (collectively,
        “Sell,” and each instance, a “Sale”) Products to addresses, entities, or individuals outside
        the United States.
      </p>
      <p className="text-indent">
        (ii) <span className="text-italic">Resale Only to End Users</span>. Customer may Sell
        Products only to end users who are under Customer’s care or who access Products through
        distribution channels or platforms authorized by this Agreement or otherwise by DFH in
        writing (“End Users”). Customer shall not Sell Products to distributors, resellers, or any
        person Customer knows or reasonably should know intends to resell Products. If Customer
        Sells Products to any person in violation of this Section 2(a), Sales by such person shall
        be attributed to and treated as if made by Customer for purposes of calculating Liquidated
        Damages set forth in Section 4(c) below.
      </p>
      <p className="text-underline">(b) Online Sales of Products</p>
      <p className="text-indent">
        (i) <span className="text-italic">Online Sales Restrictions</span>. Customer shall not,
        without the prior written consent of DFH, Sell any Products via any website or mobile
        application, except for authorized websites or mobile applications made available to
        Customer by DFH, such as E-Stores (defined below) or the{' '}
        <span className="text-italic">Well World</span>® mobile application. DFH may grant,
        withhold, or condition its consent to online sales by Customer in its sole discretion.
      </p>
      <p className="text-indent">
        (ii) <span className="text-italic">E-Stores</span>. E-Stores are e-commerce solutions that
        DFH makes available to its QHP customers including Customer so Products can be sold on their
        websites. E-Stores are accessed from the customers’ website domains and are operated by DFH
        on the customers’ behalf. All Product sales through E-Stores are between DFH and consumers
        that purchase Products through the E-Store after accessing the customers’ websites. DFH
        fulfills such orders directly to the consumers. In the event Customer instructs DFH to host
        and operate an E-Store on Customer’s website to make Products available for sale to End
        Users, Customer acknowledges and agrees that the hosting and operation of such E-Store is on
        an AS IS basis and DFH makes no representation or warranty with respect to such hosting or
        operation.
      </p>
      <p className="text-indent">
        (iii) <span className="text-italic">Practitioner Codes</span>. DFH may make available to
        Customer access codes, discount codes, or other credentials that Customer may choose to make
        available to End Users for their use to purchase Products on{' '}
        <a href="http://www.designsforhealth.com" target="_blank" rel="noreferrer">
          http://www.designsforhealth.com
        </a>{' '}
        or through another authorized website or mobile application (“Practitioner Codes”). Customer
        may disclose Practitioner Codes to its End Users but shall not post any Practitioner Code on
        a website or social media platform or otherwise disclose them in any public-facing manner.
      </p>
      <p className="text-underline">(c) Advertising & Promotion of Products by Customer</p>
      <p className="text-indent">
        (i) <span className="text-italic">Website Advertising & Promotion</span>. Customer may
        advertise and promote Products on websites owned or operated by Customer, but on no other
        websites. Advertising includes, for example and without limitation, the placement of banner
        ads, pop-up ads, tactics to promote Products in search engine shopping feeds, search engine
        optimization tactics, and sponsored searches such as Google AdWords and Google Search
        Marketing. Customer may use images or other assets provided by DFH to advertise or otherwise
        promote Products on its own websites, provided such images and assets are used in accordance
        with DFH brand use guidelines, policies and other instructions.
      </p>
      <p className="text-indent">
        (ii) <span className="text-italic">Mobile App Advertising & Promotion</span>. Customer shall
        not advertise or promote Products on mobile applications without the prior written
        authorization of DFH.
      </p>
      <p className="text-indent">
        (iii) <span className="text-italic">Social Media Advertising & Promotion</span>. Customer
        may promote Products through social media accounts owned or operated by Customer, provided
        that the posts are educational in nature and Customer otherwise complies with this
        Agreement. Customer is encouraged to interact with DFH’s posts in social media. However,
        Customer shall not engage in any paid advertising of Products on social media platforms,
        including without limitation by sponsoring posts or placing paid campaigns or other paid
        advertising activity or sweepstakes or giveaway promotion that mentions or refers to DFH,
        its brands, or the Products.
      </p>
      <p className="text-indent">
        (iv) <span className="text-italic">No Advertising or Promotion of Price</span>. Where
        advertising or promotion of Products is permitted by this Agreement (see sub-sections (i)
        and (iii) above), such advertising or promotion may not include Product pricing, promotions,
        discounts, promo codes, free shipping or any other offers affecting the price of Products.
      </p>
      <p>
        (d) <span className="text-underline">Use of DFH Materials</span>. Customer shall not use
        DFH’s trademarks, copyrighted material, or other intellectual property (“DFH Materials”)
        without prior written permission from DFH. Without limiting the foregoing, in no event shall
        Customer copy or scrape images or other content from DFH web properties or otherwise from
        online sources. If DFH grants permission to use DFH Materials, Customer shall follow DFH’s
        brand use guidelines, policies, and other instructions concerning the DFH Materials.
        Interacting with DFH Materials posted in social media in accordance with platform terms does
        not violate this Section 2(d).
      </p>
      <p>
        (e) <span className="text-underline">Product Displays</span>. Customer shall follow DFH’s
        written instructions for how and whether Customer may display Products in Customer’s place
        of business for End Users to view and purchase, including without limitation the retail
        display of Products. If Customer is a licensed pharmacist, Customer shall keep all Products,
        except for Products sold as conventional foods such as bars or Products DFH otherwise
        advises may be sold on retail shelves, behind the counter and out of the End User’s view.
      </p>
      <p>
        (f) <span className="text-underline">Product Claims</span>. Customer shall not make any
        false or misleading representations regarding Products nor make any representations or
        warranties with respect to Products that are not contained within DFH’s patient or consumer
        literature describing Products.
      </p>
      <p>
        (g) <span className="text-underline">Adverse Events; Complaints</span>. Customer shall
        promptly report to DFH, by contacting DFH Customer Experience at (860) 623-6314 or{' '}
        <a href="mailto:support@designsforhealth.com" target="_blank" rel="noreferrer">
          support@designsforhealth.com
        </a>
        , any adverse event or complaint about any Product or its use which is brought to Customer’s
        attention. Customer shall immediately report any serious adverse event to DFH so DFH may
        comply with FDA regulations concerning investigation and reporting of serious adverse
        events. Customer agrees to cooperate and provide reasonable assistance to DFH in the
        investigation of any adverse event or complaint.
      </p>
      <p>
        (h) <span className="text-underline">Prop 65.</span>. Customer acknowledges that some
        Products if sold in California would require a warning under California’s Safe Drinking
        Water and Toxic Enforcement Act of 1986, as amended, and its implementing regulations (“Prop
        65”). Such Products are “Prop 65 Products” as used herein. Prop 65 Products shipped to
        Customer at an address outside the State of California may include the label statement “Not
        labeled for sale in California” or a similar statement. A complete list of Prop 65 Products
        is available from DFH Customer Experience at (860) 623-6314 or{' '}
        <a href="mailto:support@designsforhealth.com" target="_blank" rel="noreferrer">
          support@designsforhealth.com
        </a>
        . Customer also acknowledges that it would not be practicable for DFH to pre-label Prop 65
        Products that are shipped to a non-California address with such warning given that Prop 65
        Products so shipped would typically be sold to End Users outside California. Customer
        further acknowledges that Customer will be in a better position to know which Prop 65
        Products, if any, it will Sell in California. Accordingly, Customer agrees that it will not
        Sell any Prop 65 Products to End Users in California unless Customer provides a warning or
        warnings, including warnings on its website if authorized to Sell Products online, that
        comply with the requirements of Prop 65. Note that any Prop 65 Products sent to Customer at
        a shipping address within the State of California will be labeled by DFH with an appropriate
        warning under Prop 65 and Customer agrees not to remove or alter any such warnings on any
        Product if sold in the State of California. If Customer Sells Prop 65 Products in violation
        of the foregoing, Customer shall indemnify and hold DFH harmless for any claims,
        liabilities, or losses related to any Sales of Products by Customer that do not comply with
        Prop 65, including reasonable attorneys’ fees and other court costs.
      </p>
      <p>
        <strong>3. Term & Termination</strong>
      </p>
      <p>
        (a) <span className="text-underline">Term</span>. This Agreement starts on the Effective
        Date and continues for so long as Customer’s Account is open (“Term”).
      </p>
      <p>
        (b) <span className="text-underline">Termination</span>. This Agreement terminates
        automatically with no further action of the parties when Customer’s Account is closed
        (“Termination”). DFH may close Customer’s Account with or without cause and with or without
        notice at any time in its sole discretion. Customer may close its Account by contacting DFH
        Customer Experience at (860) 623-6314 or{' '}
        <a href="mailto:support@designsforhealth.com" target="_blank" rel="noreferrer">
          support@designsforhealth.com
        </a>
        , or by other methods of contact that DFH makes available to its customers from time to
        time.
      </p>
      <p>
        (c) <span className="text-underline">Events Upon Termination</span>. Upon Termination: (i)
        DFH shall ship to Customer any Products in DFH’s inventory that have been paid for in full
        by Customer (if any), provided that Customer has no other debts outstanding to DFH; (ii) DFH
        shall otherwise stop performing under the Agreement; (iii) Customer shall be prohibited from
        reselling Products unless DFH agrees otherwise in writing, and (iv) all rights and licenses
        under this Agreement shall terminate.
      </p>
      <p>
        (d) <span className="text-underline">Termination for Breach</span>. DFH may terminate this
        Agreement immediately by written notice to Customer upon Customer’s breach of this
        Agreement, including without limitation Customer’s failure to comply with Section 2 of this
        Agreement or to maintain the qualifications provided in Section 1 above. If this Agreement
        is terminated by DFH due to Customer’s breach, in addition to the events set forth in
        Section 3(c) above, at DFH’s election: (i) all amounts Customer owes to DFH, if any, shall
        immediately become due and payable notwithstanding any payment terms or due dates to the
        contrary to which the Customer and DFH (the “Parties”) may have agreed; (ii) Customer will
        permit DFH to repurchase all Products it has on hand within fourteen (14) days of notice of
        termination, with the cost of shipment paid by Customer. The repurchase price paid by DFH
        shall be the original price actually paid by Customer less a twenty (20%) percent restocking
        fee, provided that DFH shall not pay for any Products Customer received free of charge. DFH
        shall inform Customer in its notice of termination whether it elects to repurchase any
        Products and shall pay for the repurchase within thirty (30) days of receipt of Products.
      </p>
      <p>
        (e) <span className="text-underline">Survival</span>. The following Sections of this
        RxDFHend Agreement survive termination of the Agreement, in addition to any sections in the
        Terms of Sale that by their terms survive termination: 2 (Customer’s Responsibilities); 3
        (Term & Termination); 4 (Remedies); 5 (Representations and Warranties); and 6
        (Miscellaneous).
      </p>
      <p>
        <strong>4. Remedies</strong>
      </p>
      <p>
        (a) <span className="text-underline">Remedies Cumulative</span>. Any failure to comply with
        this Agreement is deemed a material breach and may result in Termination by DFH in its sole
        discretion. DFH reserves all rights and remedies provided herein or available at law or in
        equity. Except as otherwise expressly set forth herein, all such rights and remedies are
        cumulative and not exclusive and the exercise by DFH of any right or remedy does not
        preclude the exercise of any other rights or remedies that may now or subsequently be
        available to DFH at law, in equity, by statute, in any other agreement between the Parties,
        or otherwise.
      </p>
      <p>
        (b) <span className="text-underline">Injunctive Relief</span>. Customer acknowledges that
        its breach of this Agreement will irreparably harm DFH’s brand reputation and goodwill as a
        professional line of nutraceutical products, and that the harm caused may not be susceptible
        to measurement of money damages alone. Accordingly, Customer agrees that DFH has the right
        to obtain an injunction or other equitable relief to prevent a breach or threatened breach
        of this Agreement, without the necessity of posting a bond or other security.
      </p>
      <p>
        (c) <span className="text-underline">Liquidated Damages</span>
      </p>
      <p className="text-indent">
        (i) This Section 4(c) applies to each and every breach by Customer of Sections 2(a) – 2(d)
        of this Agreement (each such breach individually and all such breaches collectively, “LD
        Breach”).
      </p>
      <p className="text-indent">(ii) Customer and DFH each acknowledge and agree that:</p>
      <p className="text-indent-2">
        (1) The terms of this Agreement are necessary and proper in order to protect DFH’s brand
        reputation and goodwill as a professional line of nutraceutical products;
      </p>
      <p className="text-indent-2">
        (2) In the event of LD Breach, DFH will be damaged and that the amount of damage to DFH will
        be difficult or impossible to determine;
      </p>
      <p className="text-indent-2">
        (3) Liquidated Damages (defined below) are intended to compensate DFH for its monetary
        damages resulting from LD Breach;
      </p>
      <p className="text-indent-2">
        (4) Liquidated Damages are intended to avoid controversy, delay, and expense in the event of
        LD Breach.
      </p>
      <p className="text-indent-2">
        (5) Liquidated Damages are estimates based on the various damages that DFH expects to suffer
        due to LD Breach, including but not limited to lost sales and lost business, the
        infringement of DFH’s trademarks and other intellectual property, the irreparable harm to
        DFH’s business, customer relationships, goodwill and quality control procedures, and the
        costs of investigating any breaches.
      </p>
      <p className="text-indent-2">
        (6) Liquidated Damages are not a penalty and are reasonable estimates of anticipated or
        actual harm that would be caused by LD Breach, the difficulty or impossibility of proving
        the amount of loss, and the difficulty or impossibility of otherwise providing an adequate
        remedy to DFH as a result of LD Breach.
      </p>
      <p className="text-indent">
        (iii){' '}
        <strong>
          Obligation to Pay Liquidated Damages. In the event of LD Breach by Customer, its owners,
          employees, agents, or subcontractors, Customer shall pay to DFH liquidated damages in an
          amount equal to $100.00 for each separate LD Breach for each day that any LD Breach occurs
          (“Liquidated Damages”).
        </strong>{' '}
        Each LD Breach with respect to each Product is considered a separate LD Breach for the
        purposes of calculating Liquidated Damages. For example, if each day for 10 days Customer
        Sells 3 Products to a reseller in breach of the prohibition on Sales to resellers in Section
        2(a)(ii) above, then that is considered 30 LD Breaches subject to Liquidated Damages of
        $3,000.00.
      </p>
      <p className="text-indent">
        (iv) Payment of Liquidated Damages is due upon written demand. DFH may, but is not required
        to, invoice Customer for any Liquidated Damages and may retain Liquidated Damages from any
        payment otherwise due to Customer. Payment or assessment of Liquidated Damages does not
        release Customer from any obligations under this Agreement.
      </p>
      <p className="text-indent">
        (v) Liquidated Damages constitute DFH&apos;s sole and exclusive monetary remedy for damages
        for LD Breaches by Customer, any of its owners, employees, agents, or subcontractors. DFH
        has the right to recover, in addition to Liquidated Damages, any damages suffered by DFH as
        a result of breaches of the Agreement other than LD Breaches. Nothing in this Section 4(c)
        limits or restricts DFH’s right to injunctive relief with respect to any LD Breach.
      </p>
      <p>
        <strong>5. Representations and Warranties.</strong> Customer represents, warrants, and
        covenants that:
      </p>
      <p className="text-indent">
        (a) any protocols, dosage, prescribing and/or usage instructions for Products provided by
        Customer to End Users, including without limitation to combine intake of Products with other
        Products or other dietary supplements or drug products, are safe and present no health or
        safety hazard to such End Users;
      </p>
      <p className="text-indent">
        (b) Customer and its authorized representatives will Sell, recommend, promote, prescribe,
        and/or administer Products in accordance with applicable laws and regulations, and in
        accordance with the applicable medical or other healthcare standard of care; and
      </p>
      <p className="text-indent">
        (c) Customer and its authorized representatives violate no law, rule, or regulation by
        executing this Agreement, opening an Account, buying and reselling Product, participating in
        any DFH rewards program, or accepting any commission or other benefit from DFH.
      </p>
      <p>
        <strong>6. Miscellaneous</strong>
      </p>
      <p>
        (a) <span className="text-underline">California Consumer Privacy Act</span>. The parties
        agree to comply with the California Consumer Privacy Act of 2018, as amended, and any
        regulations promulgated thereunder (“CCPA”). In the event that DFH processes personal
        information on behalf of Customer as part of fulfilling orders directly to End Users
        (“Customer PI”), DFH agrees that it will not (i) sell the Customer PI or (ii) retain, use or
        disclose the Customer PI for any purposes that would cause DFH to lose its status as a
        service provider. As used in this Section 6(a), “personal information,” “sell,” and “service
        provider” have the meaning given to them in the CCPA.
      </p>
      <p>
        (b) <span className="text-underline">Modifications; Waiver; Entire Agreement</span>. DFH
        shall have the right, in DFH’s sole discretion, to modify this Agreement from time to time,
        and the modified terms shall become effective on posting and acceptance by Customer.
        Otherwise, this Agreement may not be altered, amended or modified, except by a written
        document signed by an authorized representative of each Party. DFH may restrict Customer’s
        access to purchase Products, to other benefits of being a DFH practitioner, or to other
        features of the Account unless Customer agrees to the latest version of this Agreement.
        Failure by either party to enforce any of the terms and conditions of this Agreement shall
        not constitute or be deemed to be a waiver of such terms or conditions, or of the right
        thereafter to enforce the Agreement. This Agreement constitutes the entire agreement between
        the Parties with respect to the subject matter covered herein and supersedes any prior or
        contemporaneous oral or written agreements between the Parties on the same subject matter,
        except to the extent the Parties enter or have entered into a written signed agreement that
        expressly states it shall prevail in all or any respect over any future RxDFHend Agreements
        that may be formed between the Parties.
      </p>
      <p>
        (c) <span className="text-underline">Jurisdiction; Venue</span>. This Agreement is governed
        in all respects by the substantive laws of the United States of America and the State of
        Florida without regard to conflict of law principles. DFH and Customer agree that the sole
        and exclusive venue and jurisdiction for disputes arising from or related to this Agreement
        is in the U.S. District Court for the Middle District of Florida in Jacksonville or the
        state courts of Florida located in Flagler County, Florida. DFH and Customer hereby submit
        to the jurisdiction of such courts, provided however that equitable relief may be sought in
        any court having proper jurisdiction.
      </p>
      <p>
        (d) <span className="text-underline">Attorney Fees</span>. If any claim, suit, or action is
        instituted to enforce the provisions of this Agreement or otherwise with respect to the
        subject matter hereof, the prevailing party will be entitled to recover its costs and
        reasonable attorney fees incurred in the preparation, prosecution, or defense of such claim,
        suit, or action, including such costs and fees on appeal and related to the collection of
        any judgment.
      </p>
      <p>
        (e) <span className="text-underline">Notices</span>. Customer agrees that DFH may give it
        notices required by the Agreement (each a “Notice”) or otherwise respond to Customer at the
        address and/or email address DFH has on file for Customer’s Account or in any other manner
        reasonably elected by DFH. All Notices from Customer to DFH must be in writing and addressed
        to: (i){' '}
        <a href="mailto:legal@designsforhealth.com" target="_blank" rel="noreferrer">
          legal@designsforhealth.com
        </a>
        ; (ii) or “Designs for Health, Inc., 14 Commerce Blvd., Palm Coast, FL 32164, Attn: Legal
        Department; or (iii) any other address DFH otherwise designates in writing. Customer shall
        deliver all Notices by email, personal delivery, or nationally recognized overnight courier.
        Unless Notice from Customer is by email, it must be return receipt requested and postage
        prepaid. Except as otherwise provided in the Terms, a Notice is effective only if the party
        giving the Notice has complied with the requirements of this Section 6(d). Notice received
        after 5:30 p.m. on any business day will be deemed received the following business day.
      </p>
    </Styled.ContentContainer>
  )
}

RxDFHendUSMay2021Content.documentKey = legalAgreementIds.rxDFHendUSMay2021

const StyledRxDFHendUSMay2021Content = styled(RxDFHendUSMay2021Content)``

export default StyledRxDFHendUSMay2021Content
